import type {
  AiUnitBalanceState,
  AiUnitEntryKind,
  AiUnitLotSource,
  AiUnitPackVersion,
  AiUnitPaymentMethod,
  AiUnitPurchaseStatus,
  AiUnitRefundRefusal,
  PlatformAiUnitLot,
  PlatformAiUnitPurchase,
} from './api';
import type { BadgeTone } from '@/components/ui';

/**
 * Rótulos e contas dos atendimentos no backoffice (change billing-ai-units, decisão 27): a aba
 * "Pacotes" de `/precos`, a seção do tenant e a página `/atendente-ia`. As datas saem no
 * calendário de São Paulo, o mesmo do Billing.
 */

const integer = new Intl.NumberFormat('pt-BR');
const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const brl4 = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const usd2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd6 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
const saoPauloDate = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short' });
const saoPauloDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  dateStyle: 'short',
  timeStyle: 'short',
});

/** "1.000". */
export function formatUnits(units: number): string {
  return integer.format(units);
}

/** "1 atendimento", "300 atendimentos". */
export function unitsLabel(units: number): string {
  return `${integer.format(units)} ${Math.abs(units) === 1 ? 'atendimento' : 'atendimentos'}`;
}

/** "−23", "+300": a quantidade com sinal, como no extrato do dono. */
export function signedUnits(units: number): string {
  if (units > 0) return `+${integer.format(units)}`;
  if (units < 0) return `−${integer.format(-units)}`;
  return '0';
}

export function formatBrl(value: number): string {
  return brl.format(value);
}

/** Preço por atendimento: até quatro casas, para 0,3990 e 0,3330 não saírem iguais. */
export function formatUnitPrice(value: number): string {
  return brl4.format(value);
}

/** "US$ 13,19". */
export function formatUsd(value: number): string {
  return `US$ ${usd2.format(value)}`;
}

/** Tarifa por mensagem, até seis casas: "US$ 0,0068". */
export function formatUsdRate(value: number): string {
  return `US$ ${usd6.format(value)}`;
}

/** Câmbio de referência: "R$ 5,4321 por US$". */
export function formatBrlPerUsd(value: number): string {
  return `${brl4.format(value)} por US$`;
}

export function formatDate(iso: string | null | undefined): string {
  return iso ? saoPauloDate.format(new Date(iso)) : '—';
}

export function formatDateTime(iso: string | null | undefined): string {
  return iso ? saoPauloDateTime.format(new Date(iso)) : '—';
}

/** "2026-10-14" → "14/10/2026", sem passar por fuso. */
export function formatIsoDate(date: string | null | undefined): string {
  if (!date) return '—';
  const [y, m, d] = date.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : date;
}

/** "2026-10" → "10/2026". */
export function formatMonth(month: string): string {
  const [y, m] = month.split('-');
  return y && m ? `${m}/${y}` : month;
}

/** "12 meses", "1 mês". */
export function validityLabel(months: number): string {
  return months === 1 ? '1 mês' : `${integer.format(months)} meses`;
}

export function lotSourceLabel(source: AiUnitLotSource | null): string {
  switch (source) {
    case 'plan_cycle':
      return 'Cota do plano';
    case 'plan_change_proration':
      return 'Mudança de plano';
    case 'purchase':
      return 'Pacote';
    case 'concession':
      return 'Cortesia';
    case null:
      return 'Carência';
  }
}

export function entryKindLabel(kind: AiUnitEntryKind): string {
  switch (kind) {
    case 'grant':
      return 'Concessão';
    case 'consume':
      return 'Consumo';
    case 'expire':
      return 'Vencimento';
    case 'revoke':
      return 'Estorno';
    case 'restore':
      return 'Devolução';
  }
}

export function balanceStateBadge(state: AiUnitBalanceState): { label: string; tone: BadgeTone } {
  switch (state) {
    case 'ok':
      return { label: 'Com saldo', tone: 'success' };
    case 'low':
      return { label: 'Saldo baixo', tone: 'warning' };
    case 'exhausted':
      return { label: 'Sem saldo', tone: 'error' };
  }
}

export function purchaseStatusBadge(status: AiUnitPurchaseStatus): { label: string; tone: BadgeTone } {
  switch (status) {
    case 'pending':
      return { label: 'Aguardando pagamento', tone: 'warning' };
    case 'paid':
      return { label: 'Paga', tone: 'success' };
    case 'expired':
      return { label: 'Expirada', tone: 'neutral' };
    case 'refunded':
      return { label: 'Reembolsada', tone: 'info' };
    case 'charged_back':
      return { label: 'Contestada', tone: 'error' };
  }
}

export function paymentMethodLabel(method: AiUnitPaymentMethod): string {
  return method === 'pix' ? 'Pix' : 'Cartão de crédito';
}

/** Por que a compra não cabe no reembolso, como o suporte explica ao dono. */
export function refundRefusalLabel(reason: AiUnitRefundRefusal | null): string {
  switch (reason) {
    case 'not_paid':
      return 'A compra não está paga.';
    case 'window_expired':
      return 'O pagamento foi há mais de 7 dias.';
    case 'units_used':
      return 'Algum atendimento do pacote já foi usado, inclusive para descontar a carência.';
    case null:
      return 'O reembolso não cabe nas condições do pacote.';
  }
}

/** Situação de um lote: vigente (consome-se dele), fechado (estornado ou esgotado) ou vencido. */
export function lotSituation(lot: PlatformAiUnitLot): { label: string; tone: BadgeTone } {
  if (lot.isActive) return { label: 'Vigente', tone: 'success' };
  if (lot.closedAt) return { label: 'Fechado', tone: 'neutral' };
  return { label: 'Vencido', tone: 'neutral' };
}

/** A cortesia que um Admin ainda pode estornar: vigente, com sobra. */
export function isRevocableConcession(lot: PlatformAiUnitLot): boolean {
  return lot.source === 'concession' && lot.isActive && lot.remainingUnits > 0;
}

/** O que a linha da compra diz sobre o reembolso. */
export function refundSummary(purchase: PlatformAiUnitPurchase): { text: string; tone: BadgeTone } {
  const { refund } = purchase;
  switch (refund.state) {
    case 'pending_at_gateway':
      return {
        text:
          `Reembolso pendente no Asaas: o pacote já foi estornado e o sistema tenta de novo` +
          (refund.attempts > 0 ? ` (${refund.attempts} ${refund.attempts === 1 ? 'tentativa' : 'tentativas'})` : '') +
          '.',
        tone: 'warning',
      };
    case 'gave_up':
      return {
        text: 'Reembolso não concluído pelo sistema: confira no Asaas e resolva por lá.',
        tone: 'error',
      };
    case 'done':
      return { text: `Reembolsada em ${formatDateTime(refund.refundedAt)}.`, tone: 'info' };
    case 'none':
      return refund.eligible
        ? { text: `Cabe no reembolso até ${formatDateTime(refund.windowEndsAt)}.`, tone: 'success' }
        : { text: `Não cabe no reembolso: ${refundRefusalLabel(refund.refusalReason)}`, tone: 'neutral' };
  }
}

/** Situação de uma versão de pacote no histórico. */
export function packVersionSituation(
  version: AiUnitPackVersion,
  current: AiUnitPackVersion | null,
): { label: string; tone: BadgeTone } {
  if (version.cancelledByArchive) return { label: 'Cancelada pelo arquivamento', tone: 'neutral' };
  if (current && version.id === current.id) {
    return version.archivedAt ? { label: 'Vigente, arquivada', tone: 'warning' } : { label: 'Vigente', tone: 'success' };
  }
  if (!current || Date.parse(version.effectiveFrom) > Date.parse(current.effectiveFrom)) {
    return { label: 'Agendada', tone: 'info' };
  }
  return { label: 'Anterior', tone: 'neutral' };
}

/**
 * As versões agendadas de um código: as que ainda não valem e não foram canceladas pelo
 * arquivamento, da mais próxima para a mais distante. Sem vigente, toda versão não cancelada é
 * agendada.
 */
export function scheduledPackVersions(
  versions: AiUnitPackVersion[],
  current: AiUnitPackVersion | null,
): AiUnitPackVersion[] {
  const after = current ? Date.parse(current.effectiveFrom) : Number.NEGATIVE_INFINITY;
  return versions
    .filter((v) => !v.cancelledByArchive && v.id !== current?.id && Date.parse(v.effectiveFrom) > after)
    .sort((a, b) => Date.parse(a.effectiveFrom) - Date.parse(b.effectiveFrom));
}

export type DecimalInput = { state: 'empty' } | { state: 'invalid' } | { state: 'ok'; value: number };

/**
 * Lê um decimal digitado com vírgula ou ponto ("0,0068", "5.4321"), sem separador de milhar, com
 * no máximo `maxDecimals` casas. Tarifa e câmbio têm mais casas do que o campo monetário de
 * centavos aceita; o que passa da precisão da coluna é recusado aqui, e não arredondado.
 */
export function parseDecimalInput(raw: string, maxDecimals: number): DecimalInput {
  const text = raw.trim();
  if (text === '') return { state: 'empty' };
  const match = /^(\d+)(?:[.,](\d+))?$/.exec(text);
  if (!match) return { state: 'invalid' };
  const decimals = match[2] ?? '';
  if (decimals.length > maxDecimals) return { state: 'invalid' };
  const value = Number(decimals ? `${match[1]}.${decimals}` : match[1]);
  return Number.isFinite(value) ? { state: 'ok', value } : { state: 'invalid' };
}

/** Lê um inteiro positivo digitado; nulo quando não é um. */
export function parseWholeNumber(raw: string): number | null {
  const text = raw.trim();
  return /^\d+$/.test(text) ? Number(text) : null;
}

/** O padrão em que a Meta manda `pricing.category` (o mesmo da API). */
export const META_CATEGORY_PATTERN = /^[a-z_]{3,40}$/;

/** As categorias da Meta para o WhatsApp Business, sugeridas no formulário. */
export const META_CATEGORY_SUGGESTIONS = ['service', 'utility', 'marketing', 'authentication'] as const;
